import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, RefreshCw, Plus, Trash2 } from "lucide-react";

export default function Integrations() {
  const [status, setStatus] = useState(null);
  const [waLogs, setWaLogs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [wa, setWa] = useState({ to: "", message: "" });
  const [map, setMap] = useState({ talentos_field: "", hrone_field: "", module: "candidate" });

  const load = () => {
    api.get("/integrations/status").then((r) => setStatus(r.data));
    api.get("/integrations/whatsapp/logs").then((r) => setWaLogs(r.data));
    api.get("/integrations/hrone/mappings").then((r) => setMappings(r.data));
    api.get("/integrations/hrone/sync-logs").then((r) => setSyncLogs(r.data));
  };
  useEffect(() => { load(); }, []);

  const sendWa = async () => {
    if (!wa.to || !wa.message) return toast.error("Recipient and message required");
    try {
      await api.post("/integrations/whatsapp/send", wa);
      toast.success("WhatsApp message logged (simulated)");
      setWa({ to: "", message: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addMap = async () => {
    if (!map.talentos_field || !map.hrone_field) return toast.error("Both fields required");
    try {
      await api.post("/integrations/hrone/mappings", map);
      toast.success("Mapping added");
      setMap({ talentos_field: "", hrone_field: "", module: "candidate" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sync = async () => {
    try {
      await api.post("/integrations/hrone/sync");
      toast.success("HROne sync triggered (simulated)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="integrations-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Ecosystem</div>
      <h1 className="font-heading font-black text-3xl tracking-tight mt-1">Integrations</h1>

      {status && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          {Object.entries(status).map(([k, v]) => (
            <div key={k} className="border bg-white rounded-xl p-4" data-testid={`integration-status-${k}`}>
              <div className="text-xs uppercase tracking-widest font-bold capitalize">{k.replace(/_/g, " ")}</div>
              <div className="flex items-center gap-1.5 mt-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full" />
                <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">{v.mode}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground mt-2">All connectors run in SIMULATED mode — fully logged and integration-ready. Real API credentials can be wired in without changing workflows.</div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="border bg-white rounded-xl p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-4"><MessageCircle size={14} /> WhatsApp (Simulated)</div>
          <div className="flex gap-2 mb-2">
            <input data-testid="wa-to-input" value={wa.to} onChange={(e) => setWa({ ...wa, to: e.target.value })} placeholder="+91 98xxxxxxx" className="border px-3 py-2 text-sm w-40" />
            <input data-testid="wa-message-input" value={wa.message} onChange={(e) => setWa({ ...wa, message: e.target.value })} placeholder="Message" className="border px-3 py-2 text-sm flex-1" />
            <button data-testid="wa-send-button" onClick={sendWa} className="bg-[#0A251D] text-white px-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Send</button>
          </div>
          <div className="max-h-64 overflow-y-auto mt-3 space-y-2" data-testid="wa-logs">
            {waLogs.map((l) => (
              <div key={l.id} className="border p-3 text-xs">
                <div className="flex justify-between text-muted-foreground"><span>→ {l.to}</span><span>{l.status}</span></div>
                <div className="mt-1">{l.message}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border bg-white rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold"><RefreshCw size={14} /> HROne Field Mapping (Simulated)</div>
            <button data-testid="hrone-sync-button" onClick={sync} className="text-xs border border-[#0A251D] text-[#0A251D] px-3 py-1.5 hover:bg-[#0A251D] hover:text-white transition-colors duration-150">Push Hires to HROne</button>
          </div>
          <div className="flex gap-2 mb-3">
            <input data-testid="map-talentos-input" value={map.talentos_field} onChange={(e) => setMap({ ...map, talentos_field: e.target.value })} placeholder="talentos.field" className="border px-3 py-2 text-sm flex-1" />
            <span className="self-center text-muted-foreground">→</span>
            <input data-testid="map-hrone-input" value={map.hrone_field} onChange={(e) => setMap({ ...map, hrone_field: e.target.value })} placeholder="hrone.field" className="border px-3 py-2 text-sm flex-1" />
            <button data-testid="map-add-button" onClick={addMap} className="bg-[#0A251D] text-white px-3 hover:bg-[#133E31] transition-colors duration-150"><Plus size={14} /></button>
          </div>
          {mappings.map((m) => (
            <div key={m.id} className="flex items-center justify-between border-b last:border-0 py-2 text-sm">
              <span className="font-mono text-xs">{m.talentos_field} → {m.hrone_field}</span>
              <button onClick={async () => { await api.delete(`/integrations/hrone/mappings/${m.id}`); load(); }} className="text-muted-foreground hover:text-red-600"><Trash2 size={13} /></button>
            </div>
          ))}
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-5 mb-2">Sync History</div>
          {syncLogs.map((l) => (
            <div key={l.id} className="flex justify-between border-b last:border-0 py-2 text-xs">
              <span>{l.type} · {l.records} records</span>
              <span className="text-emerald-700 font-semibold">{l.status}</span>
            </div>
          ))}
          {syncLogs.length === 0 && <div className="text-xs text-muted-foreground">No syncs yet.</div>}
        </div>
      </div>
    </div>
  );
}
